import React, { useState, useCallback, useRef } from "react";
import {
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Scatter, ComposedChart, Line,
} from "recharts";

/* ============================================
   BET Surface Area Analysis — React Frontend
   Backend: Flask + pygaps
   Plots mirror pygaps calc_graphs source:
     - BET plot: all points (muted), chosen points (highlighted),
       linear fit line, monolayer marker
     - Rouquerol plot: all points (muted), chosen points (highlighted),
       monolayer marker
   ============================================ */

const API = process.env.REACT_APP_API_URL || "/api";

const C = {
  bgMain: "#f5f2ed", bgCard: "#ffffff",
  border: "#e5e0d8", borderHover: "#d4cec4",
  textPrimary: "#2c2c2c", textSecondary: "#5a5a5a", textMuted: "#8a8a8a",
  accent: "#2563eb", accentHover: "#1d4ed8",
  accentMuted: "rgba(37,99,235,0.1)",
  red: "#dc2626",
  muted: "#b0b0b0",         // "all points" color (pygaps POINTS_MUTED)
  highlighted: "#2563eb",   // "chosen points" color (pygaps POINTS_HIGHLIGHTED)
  fitLine: "#2c2c2c",       // "model fit" color (pygaps: black)
  monolayer: "#e53e3e",     // "monolayer point" color (pygaps POINTS_IMPORTANT)
};

const font = "'Outfit', -apple-system, sans-serif";
const mono = "'JetBrains Mono', monospace";

/* ─── Helpers ─── */

function StatCard({ label, value, unit }) {
  return (
    <div style={{
      background: C.bgMain, border: `1px solid ${C.border}`, borderRadius: 12,
      padding: "20px 22px", flex: "1 1 200px", minWidth: 180,
    }}>
      <div style={{ fontSize: "0.78rem", color: C.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.02em" }}>{label}</div>
      <div style={{ fontSize: "1.6rem", fontWeight: 600, color: C.textPrimary, letterSpacing: "-0.02em" }}>
        {value}
        {unit && <span style={{ fontSize: "0.8rem", fontWeight: 400, color: C.textMuted, marginLeft: 6 }}>{unit}</span>}
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div style={{
      background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12,
      padding: 28, marginBottom: 24, position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: 3, background: `linear-gradient(90deg, ${C.accent}, transparent)`, opacity: 0.6 }} />
      <h3 style={{ fontSize: "1.15rem", fontWeight: 500, color: C.textPrimary, marginBottom: 4, fontFamily: font }}>{title}</h3>
      {subtitle && <p style={{ fontSize: "0.85rem", color: C.textMuted, marginBottom: 20, lineHeight: 1.5 }}>{subtitle}</p>}
      {children}
    </div>
  );
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 8,
      padding: "10px 14px", boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      fontSize: "0.82rem", fontFamily: font,
    }}>
      {payload.filter(p => p.value != null).map((p, i) => (
        <div key={i} style={{ color: C.textSecondary, marginBottom: 2 }}>
          <span style={{ color: p.color || C.accent, fontWeight: 500 }}>{p.name}:</span>{" "}
          {typeof p.value === "number" ? p.value.toPrecision(5) : p.value}
        </div>
      ))}
    </div>
  );
}

/* ─── Main App ─── */

export default function App() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [regionMin, setRegionMin] = useState(null);
  const [regionMax, setRegionMax] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const fileRef = useRef(null);

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".aif")) {
      setError("Please upload a .aif file.");
      return;
    }
    setFileName(file.name);
    setLoading(true);
    setError(null);
    setData(null);
    setRegionMin(null);
    setRegionMax(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch(`${API}/analyze`, { method: "POST", body: formData });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `Server error ${res.status}`);
      setData(json);
      setActiveTab("overview");
    } catch (err) {
      setError(err.message || "Analysis failed.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRegionChange = useCallback((bound, delta) => {
    if (!data) return;
    const maxIdx = data.bet_plot.pressure.length - 1;
    const currentMin = regionMin ?? data.rouquerol.minimum;
    const currentMax = regionMax ?? data.rouquerol.maximum;
    if (bound === "min") {
      const newMin = currentMin + delta;
      if (newMin < 0 || newMin >= currentMax - 1) return; // need ≥3 points
      setRegionMin(newMin);
    } else {
      const newMax = currentMax + delta;
      if (newMax > maxIdx || newMax <= currentMin + 1) return;
      setRegionMax(newMax);
    }
  }, [data, regionMin, regionMax]);

  /* ─── Build chart data from API response ─── */

  // Helper: compute nice round tick values for an axis range
  const niceTicks = (min, max, targetCount = 5) => {
    if (max <= min) return [min];
    const range = max - min;
    const roughStep = range / targetCount;
    // Find a "nice" step: 1, 2, 5, 10, 20, 50... × some power of 10
    const mag = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const candidates = [1, 2, 2.5, 5, 10].map(m => m * mag);
    const step = candidates.find(s => range / s <= targetCount + 1) || candidates[candidates.length - 1];
    const start = Math.floor(min / step) * step;
    const ticks = [];
    for (let v = start; v <= max + step * 0.01; v += step) {
      ticks.push(parseFloat(v.toPrecision(8)));
    }
    return ticks;
  };

  // Padding: how many extra points to show beyond the chosen region
  const PAD = 3;

  // Chosen region indices (shared by isotherm, rouquerol, and BET plots)
  const defaultMin = data?.rouquerol.minimum;
  const defaultMax = data?.rouquerol.maximum;
  const roqMin = regionMin ?? defaultMin;
  const roqMax = regionMax ?? defaultMax;
  const isRegionModified = regionMin !== null || regionMax !== null;

  // ─── Recompute BET metrics when region changes ───
  const recomputed = data ? (() => {
    const pressure = data.bet_plot.pressure;
    const betPts = data.bet_plot.bet_points;
    const n = roqMax - roqMin + 1;
    if (n < 2) return null;

    // Linear regression: y = bet_points, x = pressure
    let sumX = 0, sumY = 0, sumXX = 0, sumXY = 0, sumYY = 0;
    for (let i = roqMin; i <= roqMax; i++) {
      const x = pressure[i], y = betPts[i];
      sumX += x; sumY += y; sumXX += x * x; sumXY += x * y; sumYY += y * y;
    }
    const meanX = sumX / n, meanY = sumY / n;
    const ssXX = sumXX - n * meanX * meanX;
    const ssXY = sumXY - n * meanX * meanY;
    const ssYY = sumYY - n * meanY * meanY;
    if (ssXX === 0) return null;
    const slope = ssXY / ssXX;
    const intercept = meanY - slope * meanX;
    if (intercept === 0 || (slope + intercept) === 0) return null;

    const corr_coef = parseFloat((ssXY / Math.sqrt(ssXX * ssYY)).toFixed(6));
    const c_const = 1 + slope / intercept;
    const n_monolayer = 1 / (slope + intercept);
    const p_monolayer = c_const > 0 ? 1 / (Math.sqrt(c_const) + 1) : data.results.p_monolayer;
    const area = (n_monolayer / data.results.n_monolayer) * data.results.area;
    const roq_monolayer = n_monolayer * (1 - p_monolayer);
    const bet_monolayer = p_monolayer / (n_monolayer * (1 - p_monolayer));

    return {
      bet_slope: slope, bet_intercept: intercept, corr_coef,
      c_const, n_monolayer, p_monolayer, area,
      roq_monolayer, bet_monolayer,
      p_min: pressure[roqMin], p_max: pressure[roqMax],
    };
  })() : null;

  // ─── PDF export ───
  const handleExportPDF = useCallback(async () => {
    if (!data) return;
    setExportLoading(true);
    try {
      const payload = {
        isotherm: data.isotherm,
        rouquerol: { ...data.rouquerol, minimum: roqMin, maximum: roqMax },
        bet_plot: { ...data.bet_plot, minimum: roqMin, maximum: roqMax },
        results: { ...data.results, ...(recomputed || {}) },
      };
      const res = await fetch(`${API}/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let msg = "Report generation failed";
        try { const err = await res.json(); msg = err.error || msg; } catch {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `BET_report_${data.isotherm.sample_id || data.isotherm.material || "sample"}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(err.message || "PDF export failed.");
    } finally {
      setExportLoading(false);
    }
  }, [data, roqMin, roqMax, recomputed]);

  // ─── Isotherm plot data (all points + chosen region + monolayer) ───
  const isothermData = data
    ? data.isotherm.pressure.map((p, i) => {
        const isChosen = i >= roqMin && i <= roqMax;
        return {
          pp0: p,
          loading: data.isotherm.loading[i],
          loadingChosen: isChosen ? data.isotherm.loading[i] : null,
        };
      })
    : [];

  // Monolayer marker: interpolate loading at p_monolayer from isotherm data
  const isoMonolayer = data ? (() => {
    const pm = recomputed?.p_monolayer ?? data.results.p_monolayer;
    const ps = data.isotherm.pressure;
    const ls = data.isotherm.loading;
    // Find bracketing index
    let idx = ps.findIndex(p => p >= pm);
    if (idx <= 0) idx = 1;
    if (idx >= ps.length) idx = ps.length - 1;
    const p0 = ps[idx - 1], p1 = ps[idx], l0 = ls[idx - 1], l1 = ls[idx];
    const t = (p1 !== p0) ? (pm - p0) / (p1 - p0) : 0;
    return [{ pp0: pm, isoMono: l0 + t * (l1 - l0) }];
  })() : [];

  // Log-scale ticks for isotherm X axis
  const isoLogTicks = data ? (() => {
    const minP = Math.min(...data.isotherm.pressure.filter(p => p > 0));
    const startExp = Math.floor(Math.log10(minP));
    const ticks = [];
    for (let e = startExp; e <= 0; e++) ticks.push(Math.pow(10, e));
    return ticks;
  })() : [];
  const isoXMin = isoLogTicks.length ? isoLogTicks[0] : 1e-4;

  // ─── Rouquerol plot data (cropped to region ± PAD) ───
  const roqViewStart = data ? Math.max(0, roqMin - PAD) : 0;
  const roqViewEnd = data ? Math.min(data.rouquerol.pressure.length - 1, roqMax + PAD) : 0;

  const roqViewData = data
    ? data.rouquerol.pressure.slice(roqViewStart, roqViewEnd + 1).map((p, i) => {
        const globalIdx = roqViewStart + i;
        const isChosen = globalIdx >= roqMin && globalIdx <= roqMax;
        return {
          pp0: p,
          roqAll: data.rouquerol.roq_points[globalIdx],
          roqChosen: isChosen ? data.rouquerol.roq_points[globalIdx] : null,
        };
      })
    : [];
  const roqMonolayer = data ? [{
    pp0: recomputed?.p_monolayer ?? data.rouquerol.p_monolayer,
    roqMono: recomputed?.roq_monolayer ?? data.rouquerol.roq_monolayer,
  }] : [];

  // Rouquerol axis ranges and ticks
  const roqXVals = roqViewData.map(d => d.pp0);
  const roqYVals = roqViewData.map(d => d.roqAll).filter(v => v != null);
  const roqXMin = roqXVals.length ? Math.min(...roqXVals) : 0;
  const roqXMax = roqXVals.length ? Math.max(...roqXVals) : 1;
  const roqYMin = roqYVals.length ? Math.min(...roqYVals) : 0;
  const roqYMax = roqYVals.length ? Math.max(...roqYVals) : 1;
  const roqXPad = (roqXMax - roqXMin) * 0.08;
  const roqYPad = (roqYMax - roqYMin) * 0.08;
  const roqXTicks = niceTicks(roqXMin - roqXPad, roqXMax + roqXPad, 6);
  const roqYTicks = niceTicks(roqYMin - roqYPad, roqYMax + roqYPad, 5);

  // Use recomputed metrics if available, otherwise fall back to API results
  const r = data ? { ...data.results, ...(recomputed || {}) } : null;

  // ─── BET plot data (cropped to region ± PAD) ───
  const betMin = roqMin;
  const betMax = roqMax;
  const betViewStart = data ? Math.max(0, betMin - PAD) : 0;
  const betViewEnd = data ? Math.min(data.bet_plot.pressure.length - 1, betMax + PAD) : 0;

  const betViewData = data
    ? data.bet_plot.pressure.slice(betViewStart, betViewEnd + 1).map((p, i) => {
        const globalIdx = betViewStart + i;
        const isChosen = globalIdx >= betMin && globalIdx <= betMax;
        return {
          pp0: p,
          betAll: data.bet_plot.bet_points[globalIdx],
          betChosen: isChosen ? data.bet_plot.bet_points[globalIdx] : null,
        };
      })
    : [];

  // BET fit line clipped to the visible x range (uses recomputed slope/intercept)
  const betFitLine = (data && r) ? (() => {
    const slope = r.bet_slope;
    const intercept = r.bet_intercept;
    const xStart = data.bet_plot.pressure[betViewStart];
    const xEnd = data.bet_plot.pressure[betViewEnd];
    return [
      { pp0: xStart, fitY: slope * xStart + intercept },
      { pp0: xEnd, fitY: slope * xEnd + intercept },
    ];
  })() : [];
  const betMonolayer = (data && r) ? [{
    pp0: r.p_monolayer,
    betMono: recomputed?.bet_monolayer ?? data.bet_plot.bet_monolayer,
  }] : [];

  // BET axis ranges and ticks
  const betXVals = betViewData.map(d => d.pp0);
  const betYVals = betViewData.map(d => d.betAll).filter(v => v != null);
  const betXMin = betXVals.length ? Math.min(...betXVals) : 0;
  const betXMax = betXVals.length ? Math.max(...betXVals) : 1;
  const betYMin = betYVals.length ? Math.min(...betYVals) : 0;
  const betYMax = betYVals.length ? Math.max(...betYVals) : 1;
  const betXPad = (betXMax - betXMin) * 0.1;
  const betYPad = (betYMax - betYMin) * 0.1;
  const betXTicks = niceTicks(Math.max(0, betXMin - betXPad), betXMax + betXPad, 6);
  const betYTicks = niceTicks(Math.max(0, betYMin - betYPad), betYMax + betYPad, 5);

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "isotherm", label: "Isotherm" },
    { id: "bet", label: "BET Plot" },
    { id: "rouquerol", label: "Rouquerol" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: C.bgMain, fontFamily: font, color: C.textSecondary }}>

      {/* Nav */}
      <nav style={{
        position: "sticky", top: 0, zIndex: 100,
        background: "rgba(245,242,237,0.9)", backdropFilter: "blur(12px)",
        borderBottom: `1px solid ${C.border}`, padding: "14px 0",
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8, background: C.accent,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "white", fontWeight: 600, fontSize: "0.85rem",
            }}>B</div>
            <span style={{ fontWeight: 500, fontSize: "1.05rem", color: C.textPrimary, letterSpacing: "-0.02em" }}>BET Analysis</span>
          </div>
          <div />
        </div>
      </nav>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 24px" }}>

        {/* Header */}
        <div style={{ marginBottom: 40 }}>
          <h1 style={{ fontSize: "clamp(1.8rem, 4vw, 2.8rem)", fontWeight: 600, color: C.textPrimary, letterSpacing: "-0.03em", lineHeight: 1.15, marginBottom: 12 }}>
            <span style={{ color: C.accent }}>BET</span> Surface Area Analysis
          </h1>
          <p style={{ fontSize: "1.05rem", color: C.textMuted, maxWidth: 620, lineHeight: 1.6 }}>
            Upload an AIF isotherm file. The backend calls{" "}
            <code style={{ fontFamily: mono, color: C.accent, fontSize: "0.95rem" }}>pygaps.characterisation.area_BET</code>{" "}
            directly. All unit conversions and Rouquerol criteria are handled by pygaps.
          </p>
        </div>

        {/* Upload */}
        <div style={{
          background: C.bgCard, border: `2px dashed ${fileName ? C.accent : C.border}`,
          borderRadius: 12, padding: "32px 28px", marginBottom: 32,
          textAlign: "center", cursor: "pointer", transition: "all 0.2s ease",
        }}
          onClick={() => fileRef.current?.click()}
          onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.accent; }}
          onDragLeave={e => { e.currentTarget.style.borderColor = fileName ? C.accent : C.border; }}
          onDrop={e => {
            e.preventDefault();
            const file = e.dataTransfer.files[0];
            if (file) { const dt = new DataTransfer(); dt.items.add(file); fileRef.current.files = dt.files; fileRef.current.dispatchEvent(new Event("change", { bubbles: true })); }
          }}
        >
          <input ref={fileRef} type="file" accept=".aif" onChange={handleFileUpload} style={{ display: "none" }} />
          <svg width="40" height="40" fill="none" stroke={C.accent} strokeWidth="1.5" viewBox="0 0 24 24" style={{ opacity: 0.7, marginBottom: 12 }}>
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
          </svg>
          {fileName
            ? <><div style={{ fontSize: "1rem", fontWeight: 500, color: C.textPrimary }}>{fileName}</div><div style={{ fontSize: "0.82rem", color: C.textMuted }}>Click or drop to replace</div></>
            : <><div style={{ fontSize: "1rem", fontWeight: 500, color: C.textPrimary }}>Drop your <span style={{ fontFamily: mono, color: C.accent }}>.aif</span> file here</div><div style={{ fontSize: "0.82rem", color: C.textMuted }}>or click to browse</div></>
          }
        </div>

        {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "14px 18px", marginBottom: 24, color: C.red, fontSize: "0.9rem" }}>⚠ {error}</div>}

        {loading && (
          <div style={{ textAlign: "center", padding: 80, color: C.textMuted }}>
            <div style={{ width: 40, height: 40, border: `3px solid ${C.border}`, borderTopColor: C.accent, borderRadius: "50%", margin: "0 auto 16px", animation: "spin 0.8s linear infinite" }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            Running BET analysis via pygaps...
          </div>
        )}

        {data && !loading && (
          <>
            {/* Tabs */}
            <div style={{ display: "flex", gap: 8, marginBottom: 28, flexWrap: "wrap" }}>
              {tabs.map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                  padding: "8px 18px",
                  background: activeTab === t.id ? C.accent : "transparent",
                  border: `1px solid ${activeTab === t.id ? C.accent : C.border}`,
                  borderRadius: 9999, color: activeTab === t.id ? "white" : C.textMuted,
                  fontFamily: font, fontSize: "0.85rem", fontWeight: activeTab === t.id ? 500 : 400,
                  cursor: "pointer", transition: "all 0.2s ease",
                }}>{t.label}</button>
              ))}
            </div>

            {/* ═══ Overview ═══ */}
            {activeTab === "overview" && (
              <div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 28 }}>
                  <StatCard label="BET Surface Area" value={r.area?.toFixed(1) ?? "—"} unit={`m²/${r.material_unit}`} />
                  <StatCard label="BET Constant (C)" value={r.c_const?.toFixed(1) ?? "—"} />
                  <StatCard label="Monolayer Capacity" value={r.n_monolayer != null ? Number(r.n_monolayer).toPrecision(4) : "—"} unit={`mol/${r.material_unit}`} />
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
                  <button
                    onClick={handleExportPDF}
                    disabled={exportLoading}
                    style={{
                      padding: "9px 22px", borderRadius: 9999,
                      background: exportLoading ? C.textMuted : C.accent,
                      color: "white", border: "none",
                      fontFamily: font, fontSize: "0.85rem", fontWeight: 500,
                      cursor: exportLoading ? "default" : "pointer",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {exportLoading ? "Generating PDF…" : "Export PDF Report"}
                  </button>
                </div>

                <ChartCard
                  title="Analysis Parameters"
                  subtitle={`${data.isotherm.material} • ${data.isotherm.adsorbate} @ ${data.isotherm.temperature} K${isRegionModified ? " • Custom region" : ""}`}
                >
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 1, background: C.border, borderRadius: 8, overflow: "hidden" }}>
                    {[
                      { label: `BET Surface Area (a)`, value: `${r.area?.toFixed(1)} m²/${r.material_unit}`, icon: "📐" },
                      { label: "BET Constant (C)", value: r.c_const?.toFixed(1), icon: "🔢" },
                      { label: "Minimum Pressure (p/p₀)", value: r.p_min, icon: "⬇",
                        stepper: true, onDec: () => handleRegionChange("min", -1), onInc: () => handleRegionChange("min", +1) },
                      { label: "Maximum Pressure (p/p₀)", value: r.p_max, icon: "⬆",
                        stepper: true, onDec: () => handleRegionChange("max", -1), onInc: () => handleRegionChange("max", +1) },
                      { label: `Monolayer Capacity (nₘ)`, value: `${Number(r.n_monolayer).toPrecision(4)} mol/${r.material_unit}`, icon: "◎" },
                      { label: "Monolayer Pressure (p/p₀)", value: r.p_monolayer, icon: "⊙" },
                      { label: "BET Slope (s)", value: Number(r.bet_slope).toPrecision(4), icon: "📈" },
                      { label: "BET Intercept (i)", value: Number(r.bet_intercept).toPrecision(4), icon: "✦" },
                      { label: "Correlation Coefficient (R)", value: r.corr_coef, icon: "📊" },
                    ].map((item, i) => (
                      <div key={i} style={{ background: C.bgCard, padding: "16px 20px", display: "flex", alignItems: "center", gap: 14 }}>
                        <span style={{ fontSize: "1.3rem", flexShrink: 0 }}>{item.icon}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: "0.78rem", color: C.textMuted, marginBottom: 2 }}>{item.label}</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {item.stepper && (
                              <button onClick={item.onDec} style={{
                                width: 28, height: 28, borderRadius: 6,
                                border: `1px solid ${C.border}`, background: C.bgMain,
                                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "1rem", color: C.textSecondary, fontFamily: mono, lineHeight: 1,
                              }}>−</button>
                            )}
                            <div style={{ fontSize: "1rem", fontWeight: 500, color: C.textPrimary, fontFamily: mono }}>{item.value ?? "—"}</div>
                            {item.stepper && (
                              <button onClick={item.onInc} style={{
                                width: 28, height: 28, borderRadius: 6,
                                border: `1px solid ${C.border}`, background: C.bgMain,
                                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "1rem", color: C.textSecondary, fontFamily: mono, lineHeight: 1,
                              }}>+</button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {isRegionModified && (
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                      <button
                        onClick={() => { setRegionMin(null); setRegionMax(null); }}
                        style={{
                          padding: "6px 16px", borderRadius: 8,
                          border: `1px solid ${C.border}`, background: C.bgMain,
                          cursor: "pointer", fontSize: "0.82rem", color: C.textSecondary,
                          fontFamily: font,
                        }}
                      >
                        Reset to pygaps defaults
                      </button>
                    </div>
                  )}
                </ChartCard>

                <ChartCard title="Method" subtitle="Backend workflow">
                  <div style={{ fontSize: "0.9rem", color: C.textSecondary, lineHeight: 1.75 }}>
                    <p style={{ marginBottom: 12 }}>
                      The backend calls <code style={{ fontFamily: mono, color: C.accent }}>pgc.area_BET(isotherm)</code> which
                      automatically selects the BET region via Rouquerol criteria. The returned dict contains
                      {" "}<code style={{ fontFamily: mono, fontSize: "0.85rem" }}>p_limit_indices</code>{" "}
                       a tuple of (min, max) indices into the pressure array, which we use to look up the actual pressure values.
                    </p>
                    <div style={{ background: C.bgMain, border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 18px", fontFamily: mono, fontSize: "0.8rem", color: C.textSecondary, overflowX: "auto" }}>
                      <div style={{ color: C.textMuted }}>## Backend (app.py)</div>
                      <div>isotherm = pgp.isotherm_from_aif(<span style={{ color: "#b45309" }}>'uploaded.aif'</span>)</div>
                      <div>bet_result = pgc.area_BET(isotherm)</div>
                      <div style={{ marginTop: 4 }}>minimum, maximum = bet_result[<span style={{ color: "#b45309" }}>"p_limit_indices"</span>]</div>
                      <div>p_min = pressure[minimum]  <span style={{ color: C.textMuted }}># → {r.p_min}</span></div>
                      <div>p_max = pressure[maximum]  <span style={{ color: C.textMuted }}># → {r.p_max}</span></div>
                    </div>
                  </div>
                </ChartCard>
              </div>
            )}

            {/* ═══ Isotherm ═══ */}
            {activeTab === "isotherm" && (
              <ChartCard
                title={`${data.isotherm.adsorbate} Adsorption Isotherm`}
                subtitle={`${data.isotherm.material} — ${data.isotherm.loading_unit}/${data.isotherm.material_unit} vs p/p₀ at ${data.isotherm.temperature} K`}
              >
                <ResponsiveContainer width="100%" height={420}>
                  <ComposedChart margin={{ top: 10, right: 30, left: 20, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis
                      dataKey="pp0" type="number"
                      scale="log"
                      domain={[isoXMin, 1]}
                      ticks={isoLogTicks}
                      tickFormatter={v => {
                        const exp = Math.round(Math.log10(v));
                        return exp === 0 ? "1" : `10${exp < 0 ? "⁻" : ""}${Math.abs(exp).toString().split("").map(d => "⁰¹²³⁴⁵⁶⁷⁸⁹"[d]).join("")}`;
                      }}
                      label={{ value: "p / p₀", position: "bottom", offset: 20, style: { fill: C.textMuted, fontSize: "0.85rem" } }}
                      tick={{ fill: C.textMuted, fontSize: "0.75rem" }}
                      stroke={C.border}
                    />
                    <YAxis
                      label={{ value: `loading / ${data.isotherm.loading_unit} ${data.isotherm.material_unit}⁻¹`, angle: -90, position: "insideLeft", offset: 0, style: { fill: C.textMuted, fontSize: "0.82rem" } }}
                      tick={{ fill: C.textMuted, fontSize: "0.75rem" }}
                      stroke={C.border}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    {/* 1. All points (muted) */}
                    <Line data={isothermData} dataKey="loading" type="monotone"
                      stroke={C.muted} strokeWidth={1.5}
                      dot={{ fill: C.muted, r: 3, strokeWidth: 0 }}
                      name="all points" />
                    {/* 2. Chosen points (highlighted) */}
                    <Line data={isothermData} dataKey="loadingChosen" type="monotone"
                      stroke={C.highlighted} strokeWidth={2.5}
                      dot={{ fill: C.highlighted, r: 5, strokeWidth: 0 }}
                      connectNulls={false}
                      name="chosen points" />
                    {/* 3. Monolayer point */}
                    <Scatter data={isoMonolayer} dataKey="isoMono"
                      fill={C.monolayer} r={7} name="monolayer point"
                      shape="diamond" />
                  </ComposedChart>
                </ResponsiveContainer>
                {/* Legend */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 12, fontSize: "0.8rem" }}>
                  {[
                    { color: C.muted, label: "all points", style: "●" },
                    { color: C.highlighted, label: "chosen region", style: "●" },
                    { color: C.monolayer, label: "monolayer point", style: "◆" },
                  ].map((l, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, color: C.textMuted }}>
                      <span style={{ color: l.color, fontSize: "1rem" }}>{l.style}</span> {l.label}
                    </div>
                  ))}
                </div>
              </ChartCard>
            )}

            {/* ═══ BET Plot (zoomed to chosen region ± context) ═══ */}
            {activeTab === "bet" && (
              <ChartCard
                title="BET Plot"
                subtitle={`Chosen region: p/p₀ = ${r.p_min} → ${r.p_max} (with ${PAD} neighbouring points for context)`}
              >
                <ResponsiveContainer width="100%" height={420}>
                  <ComposedChart margin={{ top: 10, right: 30, left: 20, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis
                      dataKey="pp0" type="number"
                      domain={[betXTicks[0], betXTicks[betXTicks.length - 1]]}
                      ticks={betXTicks}
                      tickFormatter={v => v < 0.01 ? v.toExponential(1) : v.toPrecision(3)}
                      label={{ value: "p / p₀", position: "bottom", offset: 20, style: { fill: C.textMuted, fontSize: "0.85rem" } }}
                      tick={{ fill: C.textMuted, fontSize: "0.75rem" }}
                      stroke={C.border}
                    />
                    <YAxis
                      domain={[betYTicks[0], betYTicks[betYTicks.length - 1]]}
                      ticks={betYTicks}
                      label={{ value: "(p/p₀) / [n(1 − p/p₀)]", angle: -90, position: "insideLeft", offset: 0, style: { fill: C.textMuted, fontSize: "0.78rem" } }}
                      tick={{ fill: C.textMuted, fontSize: "0.75rem" }}
                      stroke={C.border}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    {/* 1. Context points (muted) */}
                    <Line data={betViewData} dataKey="betAll" type="monotone"
                      stroke={C.muted} strokeWidth={1.5}
                      dot={{ fill: C.muted, r: 3, strokeWidth: 0 }}
                      name="context points" />
                    {/* 2. Chosen points (highlighted) */}
                    <Line data={betViewData} dataKey="betChosen" type="monotone"
                      stroke={C.highlighted} strokeWidth={2.5}
                      dot={{ fill: C.highlighted, r: 5, strokeWidth: 0 }}
                      connectNulls={false}
                      name="chosen points" />
                    {/* 3. Model fit line */}
                    <Line data={betFitLine} dataKey="fitY" type="linear"
                      stroke={C.fitLine} strokeWidth={2} strokeDasharray="6 3"
                      dot={false} name="model fit" />
                    {/* 4. Monolayer point */}
                    <Scatter data={betMonolayer} dataKey="betMono"
                      fill={C.monolayer} r={7} name="monolayer point"
                      shape="diamond" />
                  </ComposedChart>
                </ResponsiveContainer>
                {/* Legend */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 12, fontSize: "0.8rem" }}>
                  {[
                    { color: C.muted, label: "context points", style: "●" },
                    { color: C.highlighted, label: "chosen points", style: "●" },
                    { color: C.fitLine, label: "model fit", style: "╌" },
                    { color: C.monolayer, label: "monolayer point", style: "◆" },
                  ].map((l, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, color: C.textMuted }}>
                      <span style={{ color: l.color, fontSize: "1rem" }}>{l.style}</span> {l.label}
                    </div>
                  ))}
                </div>
                {/* Fit params */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 16 }}>
                  {[
                    { label: "Slope (s)", value: Number(r.bet_slope).toPrecision(4) },
                    { label: "Intercept (i)", value: Number(r.bet_intercept).toPrecision(4) },
                    { label: "C", value: r.c_const?.toFixed(1) },
                    { label: `nₘ`, value: `${Number(r.n_monolayer).toPrecision(4)} mol/${r.material_unit}` },
                    { label: "R", value: r.corr_coef },
                  ].map((p, i) => (
                    <div key={i} style={{ padding: "10px 16px", background: C.bgMain, border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: mono, fontSize: "0.82rem", flex: "1 1 130px" }}>
                      <span style={{ color: C.textMuted }}>{p.label}: </span>
                      <span style={{ color: C.textPrimary, fontWeight: 500 }}>{p.value}</span>
                    </div>
                  ))}
                </div>
              </ChartCard>
            )}

            {/* ═══ Rouquerol Plot (zoomed to chosen region ± context) ═══ */}
            {activeTab === "rouquerol" && (
              <ChartCard
                title="Rouquerol Plot"
                subtitle={`Chosen region: p/p₀ = ${r.p_min} → ${r.p_max} (with ${PAD} neighbouring points for context)`}
              >
                <ResponsiveContainer width="100%" height={420}>
                  <ComposedChart margin={{ top: 10, right: 30, left: 20, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis
                      dataKey="pp0" type="number"
                      domain={[roqXTicks[0], roqXTicks[roqXTicks.length - 1]]}
                      ticks={roqXTicks}
                      tickFormatter={v => v < 0.01 ? v.toExponential(1) : v.toPrecision(3)}
                      label={{ value: "p / p₀", position: "bottom", offset: 20, style: { fill: C.textMuted, fontSize: "0.85rem" } }}
                      tick={{ fill: C.textMuted, fontSize: "0.75rem" }}
                      stroke={C.border}
                    />
                    <YAxis
                      domain={[roqYTicks[0], roqYTicks[roqYTicks.length - 1]]}
                      ticks={roqYTicks}
                      label={{ value: "n(1 − p/p₀)", angle: -90, position: "insideLeft", offset: 0, style: { fill: C.textMuted, fontSize: "0.82rem" } }}
                      tick={{ fill: C.textMuted, fontSize: "0.75rem" }}
                      stroke={C.border}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    {/* 1. Context points (muted) */}
                    <Line data={roqViewData} dataKey="roqAll" type="monotone"
                      stroke={C.muted} strokeWidth={1.5}
                      dot={{ fill: C.muted, r: 3, strokeWidth: 0 }}
                      name="context points" />
                    {/* 2. Chosen points (highlighted) */}
                    <Line data={roqViewData} dataKey="roqChosen" type="monotone"
                      stroke={C.highlighted} strokeWidth={2.5}
                      dot={{ fill: C.highlighted, r: 5, strokeWidth: 0 }}
                      connectNulls={false}
                      name="chosen points" />
                    {/* 3. Monolayer point */}
                    <Scatter data={roqMonolayer} dataKey="roqMono"
                      fill={C.monolayer} r={7} name="monolayer point"
                      shape="diamond" />
                  </ComposedChart>
                </ResponsiveContainer>
                {/* Legend */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 12, fontSize: "0.8rem" }}>
                  {[
                    { color: C.muted, label: "context points", style: "●" },
                    { color: C.highlighted, label: "chosen points", style: "●" },
                    { color: C.monolayer, label: "monolayer point", style: "◆" },
                  ].map((l, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, color: C.textMuted }}>
                      <span style={{ color: l.color, fontSize: "1rem" }}>{l.style}</span> {l.label}
                    </div>
                  ))}
                </div>
                <div style={{
                  marginTop: 16, padding: "12px 16px", background: C.bgMain, borderRadius: 8,
                  border: `1px solid ${C.border}`, fontSize: "0.82rem", color: C.textMuted, lineHeight: 1.6,
                }}>
                  The Rouquerol criterion requires n(1 − p/p₀) to be strictly increasing within the selected BET region
                  (highlighted in blue, p/p₀ = {r.p_min} – {r.p_max}). The red diamond marks the statistical monolayer
                  at p/p₀ = {r.p_monolayer}.
                </div>
              </ChartCard>
            )}
          </>
        )}

        {!data && !loading && !error && (
          <>
            <div style={{ textAlign: "center", padding: "60px 20px 12px", color: C.textMuted, fontSize: "0.95rem" }}>
              Upload an AIF file above to begin analysis.
            </div>
            <div style={{ textAlign: "center", fontSize: "0.75rem", color: C.textMuted }}>
              Your data stays private — uploaded files are processed in memory and never stored.
            </div>
          </>
        )}

        {/* Footer */}
        <div style={{
          borderTop: `1px solid ${C.border}`, paddingTop: 24, marginTop: 40,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 12, fontSize: "0.8rem", color: C.textMuted,
        }}>
          <div>Powered by <a href="https://pygaps.readthedocs.io" target="_blank" rel="noopener noreferrer" style={{ color: C.accent, textDecoration: "none" }}>pygaps</a> · Python Gas Adsorption Processing Suite</div>
          <div style={{ fontFamily: mono }}>Built by <a href="https://jackdevans.com" target="_blank" rel="noopener noreferrer" style={{ color: C.accent, textDecoration: "none" }}>Jack D. Evans</a></div>
        </div>
      </div>
    </div>
  );
}
